import { OWNER_STAFF_ID, canPerform, salonDayFor, staffName } from './salonDays'
import type { Viewer } from './staffAccess'
import type { AgendaAppointment } from '../types/agenda'
import type { OpeningDay, SalonDayException, StaffProfile } from '../types/site'

/**
 * Wat een collega zelf mag in de agenda (docs/KASSA-ABONNEMENTEN.md §4). Puur, getest; de server
 * dwingt hetzelfde af (403/422). De eigenaar mag alles: zij plant bewust ook buiten de tijden.
 */

type Hours = { hours: readonly OpeningDay[]; exceptions: readonly SalonDayException[] | undefined; staff: readonly StaffProfile[] }

/**
 * Mag deze kijker op `date` een afspraak voor `treatmentSlug` inplannen (vervolgafspraak)?
 * Een collega alleen op haar eigen salondagen en voor behandelingen die zij doet. `null` = mag.
 */
export function bookingProblem(viewer: Viewer, b: { date: string; treatmentSlug: string }, h: Hours): string | null {
  if (viewer.isOwner) return null
  const day = salonDayFor(h.hours, h.exceptions, b.date)
  if (!day) return 'De salon is die dag dicht.'
  if (day.staffId !== viewer.staffId) return `Die dag werkt ${staffName(h.staff, day.staffId)}. Kies een van je eigen dagen.`
  if (!canPerform(h.staff, viewer.staffId, b.treatmentSlug)) return 'Deze behandeling doe jij niet.'
  return null
}

/**
 * Mag deze kijker een aanvraag bevestigen of een andere tijd voorstellen? Een collega alleen als de
 * aanvraag bij haar staat én op haar eigen salondag valt. Een nieuw voorstel moet ook weer op haar
 * eigen dag (zie `bookingProblem`). `null` = mag.
 */
export function requestProblem(viewer: Viewer, a: Pick<AgendaAppointment, 'date' | 'staffId' | 'treatmentSlug'>, h: Hours): string | null {
  if (viewer.isOwner) return null
  if ((a.staffId || OWNER_STAFF_ID) !== viewer.staffId) return 'Deze aanvraag is niet voor jou.'
  return bookingProblem(viewer, a, h)
}

/** Vervolgafspraak: zelfde tijd, `weeks` weken later. */
export function followUpDate(date: string, weeks: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const next = new Date(y, m - 1, d + weeks * 7, 12)
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`
}

/** De voorgestelde termijnen bij "Plan vervolg". */
export const FOLLOW_UP_WEEKS = [2, 4, 6, 8] as const

/**
 * Een kuursessie bij een andere behandelaar dan bij wie de kuur liep: een waarschuwing, geen
 * blokkade. Leeg = geen waarschuwing (geen kuur, geen vaste behandelaar, of dezelfde persoon).
 */
export function courseWarning(courseStaffId: string | undefined, performerId: string | undefined, staff: readonly StaffProfile[]): string {
  if (!courseStaffId) return ''
  const performer = performerId || OWNER_STAFF_ID
  if (courseStaffId === performer) return ''
  return `Haar kuur loopt bij ${staffName(staff, courseStaffId)}; deze sessie doet ${staffName(staff, performer)}.`
}
